import { ColumnDef } from '@tanstack/react-table';
import { EllipsisVertical } from 'lucide-react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import type { ColumnFilterConfig, MobileCardRole } from '@/lib/table-query';
import { formatMomentDay } from '../application-utils';
import { ApplicationBase } from '../service-desk.types';
import { StatusBadge, statusLabelKey } from './status-badge';

type Translate = (key: string) => string;

/**
 * Every column below that shows a field of the application schema has that field as its `id`, so
 * sorting and its header filter run on the server (see `useApplicationsTable`).
 */
interface TextColumnOptions {
  /** The header filter; free text by default, `false` for none. */
  filter?: ColumnFilterConfig | false;
  /** Off for a column without a field of its own (a name looked up from an id). */
  sortable?: boolean;
  /** Its place on the phone card; the applicant's `Name` is the card's title by default. */
  mobile?: MobileCardRole;
  /** Its position among the card's body values (see `ColumnMeta.mobileOrder`). */
  mobileOrder?: number;
}

/** On a phone card the applicant's name is the title (with the applied date under it). */
export const NAME_ON_CARD = { mobile: 'title' } as const;

export const textColumn = <TItem,>(
  t: Translate,
  id: string,
  titleKey: string,
  accessor: (row: TItem) => string | undefined | null,
  {
    filter = { type: 'text' },
    sortable = true,
    mobile = id === 'Name' ? 'title' : undefined,
    mobileOrder,
  }: TextColumnOptions = {}
): ColumnDef<TItem> => ({
  id,
  meta: {
    ...(filter ? { filter } : {}),
    ...(mobile ? { mobile } : {}),
    ...(mobileOrder !== undefined ? { mobileOrder } : {}),
  },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t(titleKey)} />,
  accessorFn: (row) => accessor(row) || '-',
  enableSorting: sortable,
});

/** An `options` header filter over stored values, labelled with their UILM keys. */
export const optionsFilter = (
  t: Translate,
  options: readonly { value: string; labelKey: string }[]
): ColumnFilterConfig => ({
  type: 'options',
  options: options.map((option) => ({ value: option.value, label: t(option.labelKey) })),
});

/**
 * The day an application was submitted, in the record's own `Timezone`; filtered by days of the
 * organization's zone (`AppliedDateUtc` is a UTC moment). `cell` restyles the value (BYOD).
 */
export const appliedDateColumn = <TItem extends ApplicationBase>(
  t: Translate,
  titleKey: string,
  cell?: ColumnDef<TItem>['cell']
): ColumnDef<TItem> => ({
  id: 'AppliedDateUtc',
  // Card: under the title (the applicant's name, or the application's type in "My Applications").
  meta: { filter: { type: 'datetime' }, mobile: 'subtitle' },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t(titleKey)} />,
  accessorFn: (row) => formatMomentDay(row.AppliedDateUtc, row.Timezone),
  ...(cell ? { cell } : {}),
});

/** The column with its place on the phone card set (its filter and the rest of `meta` kept). */
export const withCard = <TItem,>(
  column: ColumnDef<TItem>,
  card: Pick<NonNullable<ColumnDef<TItem>['meta']>, 'mobile' | 'mobileOrder' | 'cardOnly'>
): ColumnDef<TItem> => ({ ...column, meta: { ...column.meta, ...card } });

/** A department a reviewer may see (`useDepartmentScope().departments`). */
export interface DepartmentOption {
  ItemId: string;
  Title: string;
}

/**
 * The applicant's department (the `DepartmentId` stamped when applying), shown by name and filtered
 * by the departments the reviewer may see. The list's department-scope clause still applies on top
 * (`mergeWhere` ANDs the two), so picking one never widens what a scoped reviewer sees.
 */
export const departmentColumn = <TItem extends { DepartmentId?: string }>(
  t: Translate,
  departments: readonly DepartmentOption[],
  /** The name stored on the application, shown when its `DepartmentId` is not a known one. */
  storedNameOf?: (row: TItem) => string | undefined | null
): ColumnDef<TItem> => ({
  id: 'DepartmentId',
  meta: {
    filter: {
      type: 'options',
      // A searchable dropdown: the department list is long.
      dropdown: true,
      options: departments.map((department) => ({
        value: department.ItemId,
        label: department.Title,
      })),
    },
  },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
  accessorFn: (row) =>
    departments.find((department) => department.ItemId === row.DepartmentId)?.Title ||
    storedNameOf?.(row) ||
    '-',
  // Stored as an id, so sorting it would not follow the names.
  enableSorting: false,
});

/**
 * The application's status, filtered by the module's `statuses`. A badge by default; the requests
 * lists that change the status in place pass a `cell` with the status select.
 */
export const statusColumn = <TItem extends { Status: string }>(
  t: Translate,
  statuses: readonly string[],
  cell: ColumnDef<TItem>['cell'] = ({ row }) => <StatusBadge status={row.original.Status} />
): ColumnDef<TItem> => ({
  id: 'Status',
  meta: {
    filter: optionsFilter(
      t,
      statuses.map((status) => ({ value: status, labelKey: statusLabelKey(status) }))
    ),
  },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
  accessorFn: (row) => row.Status,
  cell,
});

export interface RowAction {
  label: string;
  onSelect: () => void;
  /** Hidden actions are dropped, so callers can list every action and gate each on a condition. */
  hidden?: boolean;
  destructive?: boolean;
}

/** Kebab menu of per-row actions; renders nothing when no action is available for the row. */
const RowActions = ({ actions }: { actions: RowAction[] }) => {
  const visible = actions.filter((action) => !action.hidden);
  if (!visible.length) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" onClick={(event) => event.stopPropagation()}>
          <EllipsisVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      {/* React bubbles portal events to the row, so without this a menu click also opens the row's details. */}
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        {visible.map((action) => (
          <DropdownMenuItem
            key={action.label}
            className={action.destructive ? 'text-destructive' : undefined}
            onSelect={action.onSelect}
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const actionsColumn = <TItem,>(
  t: Translate,
  actionsFor: (row: TItem) => RowAction[]
): ColumnDef<TItem> => ({
  id: 'actions',
  // Card: top right, as the old portal's card header.
  meta: { mobile: 'badge' as const },
  header: () => <span className="text-xs font-medium">{t('ACTIONS')}</span>,
  enableSorting: false,
  enableHiding: false,
  cell: ({ row }) => <RowActions actions={actionsFor(row.original)} />,
});
